import React, { useState, useEffect, useCallback } from 'react';
import { vendorsAPI } from '../lib/api';
import { formatINR, apiError } from '../lib/utils';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Search, Edit2, Trash2, Loader2, Truck } from 'lucide-react';

const PAGE_SIZE = 24;

const EMPTY_FORM = () => ({
  name: '',
  email: '',
  phone: '',
  gstin: '',
  pan: '',
  address: '',
  city: '',
  state: '',
  zip: '',
  notes: '',
});

export default function Vendors() {
  const { can } = useAuth();
  const canWrite = can('write');
  const canDelete = can('delete');

  const [vendors, setVendors] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(1);

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM());
  const [saving, setSaving] = useState(false);

  const fetchVendors = useCallback(async () => {
    try {
      const res = await vendorsAPI.getAll();
      setVendors(res.data);
    } catch (err) {
      toast.error(apiError(err, 'Failed to fetch vendors'));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchVendors(); }, [fetchVendors]);

  const openNew = () => {
    setEditing(null);
    setForm(EMPTY_FORM());
    setDialogOpen(true);
  };

  const openEdit = (v) => {
    setEditing(v);
    const f = EMPTY_FORM();
    Object.keys(f).forEach((k) => { f[k] = v[k] || ''; });
    setForm(f);
    setDialogOpen(true);
  };

  const set = (field) => (e) => setForm({ ...form, [field]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    const body = { ...form, name: form.name.trim() };
    if (!body.name) return toast.error('Vendor name is required');
    setSaving(true);
    try {
      if (editing) {
        await vendorsAPI.update(editing.id, body);
        toast.success('Vendor updated');
      } else {
        await vendorsAPI.create(body);
        toast.success('Vendor added');
      }
      setDialogOpen(false);
      fetchVendors();
    } catch (err) {
      // 409 duplicate name carries a readable `detail`
      toast.error(apiError(err, 'Failed to save vendor'));
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (v) => {
    if (!window.confirm(`Delete vendor "${v.name}"? This cannot be undone.`)) return;
    try {
      await vendorsAPI.delete(v.id);
      toast.success('Vendor deleted');
      fetchVendors();
    } catch (err) {
      toast.error(apiError(err, 'Delete failed'));
    }
  };

  const filtered = vendors.filter((v) => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return true;
    return [v.name, v.email, v.phone, v.gstin, v.pan, v.city].join(' ').toLowerCase().includes(q);
  });

  useEffect(() => {
    setPage(1);
  }, [searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  // Deleting the last vendor on the last page would otherwise strand us on an empty page.
  useEffect(() => {
    if (page > totalPages) setPage(totalPages);
  }, [page, totalPages]);
  const paged = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);
  const showingFrom = filtered.length === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const showingTo = Math.min(page * PAGE_SIZE, filtered.length);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[60vh]">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
      </div>
    );
  }

  return (
    <div className="space-y-8 animate-fadeIn">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="text-3xl font-bold font-['Manrope'] tracking-tight">Vendors</h1>
          <p className="text-gray-500 mt-1">Suppliers and service providers you pay</p>
        </div>
        {canWrite && (
          <Button data-testid="add-vendor-btn" className="bg-black hover:bg-black/90" onClick={openNew}>
            <Plus className="w-4 h-4 mr-2" />
            Add Vendor
          </Button>
        )}
      </div>

      {/* Search */}
      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
        <Input
          data-testid="vendor-search"
          placeholder="Search vendors by name, phone, email, GSTIN or PAN"
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Table */}
      <Card>
        <CardContent className="p-0">
          <div className="overflow-x-auto">
            <table className="w-full data-table">
              <thead>
                <tr>
                  <th className="table-header text-left">Name</th>
                  <th className="table-header text-left hidden md:table-cell">Phone</th>
                  <th className="table-header text-left hidden md:table-cell">Email</th>
                  <th className="table-header text-left hidden md:table-cell">GSTIN</th>
                  <th className="table-header text-right">Total paid</th>
                  <th className="table-header text-center">Actions</th>
                </tr>
              </thead>
              <tbody>
                {paged.length > 0 ? (
                  paged.map((v) => (
                    <tr key={v.id} className="table-row" data-testid={`vendor-row-${v.id}`}>
                      <td className="table-cell">
                        <p className="font-medium break-words">{v.name}</p>
                        {v.city && <p className="text-xs text-gray-500">{v.city}</p>}
                        {(v.phone || v.email) && <p className="md:hidden text-xs text-gray-500 break-all">{[v.phone, v.email].filter(Boolean).join(' · ')}</p>}
                      </td>
                      <td className="table-cell hidden md:table-cell">{v.phone || '-'}</td>
                      <td className="table-cell hidden md:table-cell">{v.email || '-'}</td>
                      <td className="table-cell font-mono text-xs hidden md:table-cell">{v.gstin || '-'}</td>
                      <td className="table-cell text-right font-mono whitespace-nowrap">{formatINR(v.total_paid)}</td>
                      <td className="table-cell">
                        <div className="flex items-center justify-center gap-1">
                          {canWrite && (
                            <Button variant="ghost" size="sm" data-testid={`edit-vendor-${v.id}`} onClick={() => openEdit(v)} title="Edit">
                              <Edit2 className="w-4 h-4" />
                            </Button>
                          )}
                          {canDelete && (
                            <Button
                              variant="ghost"
                              size="sm"
                              data-testid={`delete-vendor-${v.id}`}
                              onClick={() => handleDelete(v)}
                              className="text-red-600 hover:text-red-700 hover:bg-red-50"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center py-12 text-gray-400">
                      <Truck className="w-12 h-12 mx-auto mb-4 opacity-50" />
                      <p>{searchTerm ? 'No vendors match your search.' : 'No vendors yet'}</p>
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>

      {/* Pagination */}
      {filtered.length > PAGE_SIZE && (
        <div className="flex items-center justify-between">
          <p className="text-sm text-gray-500">
            Showing {showingFrom}–{showingTo} of {filtered.length}
          </p>
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
              Prev
            </Button>
            <span className="text-sm text-gray-600">Page {page} of {totalPages}</span>
            <Button variant="outline" size="sm" onClick={() => setPage((p) => Math.min(totalPages, p + 1))} disabled={page === totalPages}>
              Next
            </Button>
          </div>
        </div>
      )}

      {/* Add / edit */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{editing ? 'Edit Vendor' : 'Add New Vendor'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-6 mt-4">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="vendor-name">Vendor Name *</Label>
                <Input id="vendor-name" data-testid="vendor-name-input" value={form.name} onChange={set('name')} required />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor-phone">Phone</Label>
                <Input id="vendor-phone" value={form.phone} onChange={set('phone')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor-email">Email</Label>
                <Input id="vendor-email" type="email" value={form.email} onChange={set('email')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor-gstin">GSTIN</Label>
                <Input
                  id="vendor-gstin"
                  className="font-mono"
                  value={form.gstin}
                  onChange={(e) => setForm({ ...form, gstin: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor-pan">PAN</Label>
                <Input
                  id="vendor-pan"
                  className="font-mono"
                  value={form.pan}
                  onChange={(e) => setForm({ ...form, pan: e.target.value.toUpperCase() })}
                />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="vendor-address">Address</Label>
                <Textarea id="vendor-address" rows={2} value={form.address} onChange={set('address')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor-city">City</Label>
                <Input id="vendor-city" value={form.city} onChange={set('city')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor-state">State</Label>
                <Input id="vendor-state" value={form.state} onChange={set('state')} />
              </div>
              <div className="space-y-2">
                <Label htmlFor="vendor-zip">PIN / ZIP</Label>
                <Input id="vendor-zip" value={form.zip} onChange={set('zip')} />
              </div>
              <div className="space-y-2 sm:col-span-2">
                <Label htmlFor="vendor-notes">Notes</Label>
                <Textarea id="vendor-notes" rows={2} value={form.notes} onChange={set('notes')} />
              </div>
            </div>

            <div className="flex justify-end gap-3 pt-4 border-t">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving} className="bg-black hover:bg-black/90" data-testid="save-vendor-btn">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : editing ? 'Update' : 'Add Vendor'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
